const CONTACT_ALLOWED_TYPES = ['image/jpeg', 'image/png', 'image/webp', 'image/heic', 'application/pdf'];
const CONTACT_EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;
// English labels regardless of site_language, matching contact.topics.* in en.default.json.
const CONTACT_TOPIC_LABELS = {
  order: 'Orders',
  returns: 'Returns',
  repairs: 'Repairs',
  product: 'Product information',
  stores: 'Stores & boutiques',
  other: 'Other',
};

function sha256Hex(text) {
  return crypto.subtle.digest('SHA-256', new TextEncoder().encode(text)).then((buffer) =>
    Array.from(new Uint8Array(buffer))
      .map((b) => b.toString(16).padStart(2, '0'))
      .join('')
  );
}

class GzContactForm extends HTMLElement {
  connectedCallback() {
    this.endpoint = (this.dataset.endpoint || '').trim();
    this.recaptchaSiteKey = (this.dataset.recaptchaSiteKey || '').trim();
    this.locale = this.dataset.locale || '';
    this.market = this.dataset.market || '';
    this.maxFiles = Number(this.dataset.maxFiles) || 5;
    this.maxSize = Number(this.dataset.maxSize) || 10485760;

    this.form = this.querySelector('.js-contact-form');
    if (!this.form) return;

    this.fileInput = this.querySelector('.js-contact-files');
    this.fileList = this.querySelector('.js-contact-file-list');
    this.counter = this.querySelector('.js-counter');
    this.messageInput = this.querySelector('#ContactMessage');
    this.errorEl = this.querySelector('.js-contact-error');
    this.successEl = this.querySelector('.js-contact-success');
    this.submitBtn = this.querySelector('.js-contact-submit');
    this.submitLabel = this.querySelector('.js-contact-submit-label');
    this.recaptchaInput = this.querySelector('#g-recaptcha-response');

    this.strings = this.readStrings();
    this.defaultSubmitLabel = this.submitLabel ? this.submitLabel.textContent.trim() : '';
    this.files = [];
    this.isSubmitting = false;

    this.bindEvents();

    if (!this.endpoint) {
      console.warn('gz-contact-form: endpoint non configurato (Impostazioni tema > Contact form).');
      if (this.submitBtn) this.submitBtn.disabled = true;
    }

    if (!this.recaptchaSiteKey) {
      console.warn('gz-contact-form: reCAPTCHA site key non configurata (Impostazioni tema > Contact form).');
    }
  }

  readStrings() {
    const el = this.querySelector('.js-contact-strings');
    try {
      return JSON.parse(el.textContent);
    } catch {
      return {};
    }
  }

  bindEvents() {
    this.form.addEventListener('submit', this.onSubmit.bind(this));

    if (this.messageInput && this.counter) {
      const max = this.messageInput.getAttribute('maxlength') || 500;
      this.messageInput.addEventListener('input', () => {
        this.counter.textContent = `${this.messageInput.value.length}/${max}`;
      });
    }

    if (this.fileInput) {
      this.fileInput.addEventListener('change', this.onFilesChange.bind(this));
    }

    this.form.addEventListener('input', (event) => this.clearFieldError(event.target));
    this.form.addEventListener('change', (event) => this.clearFieldError(event.target));
  }

  onFilesChange() {
    const selected = Array.from(this.fileInput.files || []);
    this.clearError();

    if (selected.length > this.maxFiles) {
      this.showError(this.strings.too_many_files);
      this.fileInput.value = '';
      this.files = [];
      this.renderFileList();
      return;
    }

    const oversize = selected.find((file) => file.size > this.maxSize);
    if (oversize) {
      this.showError(this.strings.file_too_large);
      this.fileInput.value = '';
      this.files = [];
      this.renderFileList();
      return;
    }

    const unsupported = selected.find((file) => !CONTACT_ALLOWED_TYPES.includes(file.type));
    if (unsupported) {
      this.showError(this.strings.unsupported_type);
      this.fileInput.value = '';
      this.files = [];
      this.renderFileList();
      return;
    }

    this.files = selected;
    this.renderFileList();
  }

  renderFileList() {
    if (!this.fileList) return;
    this.fileList.textContent = this.files.length
      ? this.files.map((file) => file.name).join(', ')
      : this.strings.no_file;
  }

  collect() {
    const value = (selector) => (this.querySelector(selector)?.value || '').trim();
    return {
      topic: value('#ContactTopic'),
      first_name: value('#ContactFirstName'),
      last_name: value('#ContactLastName'),
      phone: value('#ContactPhone'),
      email: value('#ContactEmail'),
      message: value('#ContactMessage'),
      privacy: this.querySelector('.js-contact-privacy')?.checked || false,
      company: value('#ContactCompany'),
    };
  }

  validate(fields) {
    const invalid = [];
    const required = {
      '#ContactTopic': fields.topic,
      '#ContactFirstName': fields.first_name,
      '#ContactLastName': fields.last_name,
      '#ContactPhone': fields.phone,
      '#ContactEmail': fields.email,
      '#ContactMessage': fields.message,
    };

    for (const [selector, value] of Object.entries(required)) {
      if (!value) invalid.push({ selector, message: this.strings.required });
    }

    if (fields.email && !CONTACT_EMAIL_RE.test(fields.email)) {
      invalid.push({ selector: '#ContactEmail', message: this.strings.invalid_email });
    }
    if (!fields.privacy) {
      invalid.push({ selector: '.js-contact-privacy', message: this.strings.privacy });
    }

    return invalid;
  }

  markFieldError(selector) {
    const el = this.querySelector(selector);
    if (!el) return;
    el.setAttribute('aria-invalid', 'true');
    el.parentElement?.classList.add('border-red-600!');
  }

  clearFieldError(el) {
    if (!el || !el.parentElement) return;
    el.removeAttribute('aria-invalid');
    el.parentElement.classList.remove('border-red-600!');
  }

  clearErrors() {
    this.clearError();
    this.querySelectorAll('[aria-invalid]').forEach((el) => this.clearFieldError(el));
  }

  clearError() {
    if (!this.errorEl) return;
    this.errorEl.textContent = '';
    this.errorEl.classList.add('hidden');
  }

  showError(message) {
    if (!this.errorEl) return;
    this.errorEl.textContent = message || this.strings.generic;
    this.errorEl.classList.remove('hidden');
  }

  setLoading(loading) {
    this.isSubmitting = loading;
    if (this.submitBtn) this.submitBtn.disabled = loading;
    if (this.submitLabel) {
      this.submitLabel.textContent = loading ? this.strings.sending : this.defaultSubmitLabel;
    }
  }

  showSuccess() {
    this.form.classList.add('hidden');
    if (this.successEl) {
      this.successEl.textContent = this.strings.success;
      this.successEl.classList.remove('hidden');
    }
  }

  pushContactFormAnalytics(fields) {
    const context = window.GZDataLayerContext || {};
    const topic = CONTACT_TOPIC_LABELS[fields.topic] || fields.topic;

    window.GZAnalyticsQueue = window.GZAnalyticsQueue || [];

    if (context.login_status === 'logged') {
      window.GZAnalyticsQueue.push((GZAnalytics) => {
        GZAnalytics.push({
          event: 'submit_contact_form',
          topic,
          emailSHA256: context.emailSHA256,
          hash_method: context.hash_method,
          customer_id: context.customer_id,
        });
      });
      return;
    }

    sha256Hex(fields.email).then((emailSHA256) => {
      window.GZAnalyticsQueue.push((GZAnalytics) => {
        GZAnalytics.push({
          event: 'submit_contact_form',
          topic,
          emailSHA256,
          hash_method: 'SHA256',
          customer_id: null,
        });
      });
    });
  }

  getRecaptchaToken() {
    return new Promise((resolve, reject) => {
      if (!this.recaptchaSiteKey) {
        reject(new Error('recaptcha_missing'));
        return;
      }
      if (typeof grecaptcha === 'undefined') {
        reject(new Error('recaptcha_unavailable'));
        return;
      }

      grecaptcha.ready(() => {
        grecaptcha
          .execute(this.recaptchaSiteKey, { action: 'submit' })
          .then(resolve)
          .catch(reject);
      });
    });
  }

  buildFormData(fields, token) {
    const formData = new FormData();

    formData.append('topic', fields.topic);
    formData.append('first_name', fields.first_name);
    formData.append('last_name', fields.last_name);
    formData.append('phone', fields.phone);
    formData.append('email', fields.email);
    formData.append('message', fields.message);
    formData.append('privacy', fields.privacy ? 'true' : 'false');
    formData.append('company', fields.company);
    formData.append('locale', this.locale);
    formData.append('market', this.market);
    formData.append('pageUrl', window.location.href);
    formData.append('g-recaptcha-response', token);

    this.files.forEach((file) => {
      formData.append('file_upload', file, file.name);
    });

    return formData;
  }

  async onSubmit(event) {
    event.preventDefault();
    if (this.isSubmitting) return;

    this.clearErrors();

    const fields = this.collect();
    const invalid = this.validate(fields);
    if (invalid.length) {
      invalid.forEach(({ selector }) => this.markFieldError(selector));
      this.showError(invalid[0].message);
      this.querySelector(invalid[0].selector)?.focus();
      return;
    }

    if (!this.endpoint) {
      this.showError(this.strings.generic);
      return;
    }

    this.setLoading(true);

    try {
      const token = await this.getRecaptchaToken();
      if (this.recaptchaInput) this.recaptchaInput.value = token;

      const response = await fetch(this.endpoint, {
        method: 'POST',
        body: this.buildFormData(fields, token),
        headers: { Accept: 'application/json' },
      });

      if (!response.ok) throw new Error(`submit ${response.status}`);

      this.showSuccess();
      this.pushContactFormAnalytics(fields);
    } catch (error) {
      console.error('gz-contact-form: invio fallito', error);
      this.showError(this.strings.generic);
      this.setLoading(false);
    }
  }
}

if (!customElements.get('gz-contact-form')) {
  customElements.define('gz-contact-form', GzContactForm);
}
